import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Send, Inbox, Plus, UserPlus, ChevronRight } from "lucide-react";
import AppLayout from "../../components/AppLayout";
import TrialBanner from "../../components/TrialBanner";
import BetaBanner from "../../components/BetaBanner";
import OnboardingChecklist from "../../components/OnboardingChecklist";
import { useAuth } from "../../lib/AuthContext";
import api, { formatCurrency } from "../../lib/api";
import { recordDashboardVisit } from "../../lib/installMilestone";
import { useTerms } from "../../lib/BrandContext";

/* ─────────────────────────────────────────────── */
/* Today's Snapshot — three coach-friendly numbers */
/* ─────────────────────────────────────────────── */
function SnapshotRow({ label, value, tone = "ink", testid }) {
  const toneClass =
    tone === "rose"    ? "text-rose-700"    :
    tone === "amber"   ? "text-amber-700"   :
    tone === "emerald" ? "text-emerald-700" :
    "text-ink-900";
  return (
    <div className="flex items-baseline justify-between gap-4 py-3" data-testid={testid}>
      <span className="text-sm font-medium text-ink-600">{label}</span>
      <span className={`font-heading text-2xl font-bold ${toneClass}`}>{value}</span>
    </div>
  );
}

/* ──────────────────────────────────────────────── */
/* Big primary action button — clipboard-style row  */
/* ──────────────────────────────────────────────── */
function ActionButton({ to, onClick, icon: Icon, label, sub, accent = "brand", testid }) {
  const ring =
    accent === "amber"   ? "border-amber-200 bg-amber-50 hover:bg-amber-100"   :
    accent === "rose"    ? "border-rose-200 bg-rose-50 hover:bg-rose-100"      :
    accent === "ink"     ? "border-ink-200 bg-white hover:bg-ink-100"          :
    "border-brand-100 bg-brand-50 hover:bg-brand-100";
  const iconBg =
    accent === "amber"   ? "bg-amber-500 text-white"   :
    accent === "rose"    ? "bg-rose-600 text-white"    :
    accent === "ink"     ? "bg-ink-900 text-white"     :
    "bg-brand text-white";

  const inner = (
    <div className={`flex items-center gap-4 rounded-2xl border p-5 transition-colors ${ring}`}>
      <span className={`inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${iconBg}`}>
        <Icon className="h-6 w-6" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-heading text-lg font-bold text-ink-900">{label}</p>
        {sub ? <p className="text-sm text-ink-600">{sub}</p> : null}
      </div>
      <ChevronRight className="h-5 w-5 shrink-0 text-ink-400" />
    </div>
  );

  if (to) {
    return <Link to={to} data-testid={testid} className="block focus:outline-none">{inner}</Link>;
  }
  return (
    <button type="button" onClick={onClick} data-testid={testid} className="block w-full text-left focus:outline-none">
      {inner}
    </button>
  );
}

export default function CoachDashboard() {
  const { user } = useAuth();
  const { terms } = useTerms();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const visitRecorded = useRef(false);

  useEffect(() => {
    // Count this dashboard visit for the install-prompt fallback exactly
    // once per real mount. useRef guards against React StrictMode's
    // dev-only double-invoke of effects.
    if (!visitRecorded.current) {
      visitRecorded.current = true;
      recordDashboardVisit();
    }
    api.get("/coach/dashboard")
      .then((r) => setData(r.data))
      .catch(() => { setError("Couldn't load your home screen."); setData({ totals: {}, upcoming: [], activity: [] }); });
  }, []);

  if (!data) {
    return (
      <AppLayout>
        <div className="flex h-64 items-center justify-center text-ink-500" data-testid="dashboard-loading">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand border-t-transparent" />
        </div>
      </AppLayout>
    );
  }

  const t = data?.totals || {};
  const plan = data?.plan;
  const firstName = user?.name?.split(" ")[0] || terms.leader;

  // For "Add Fee" / "Add Player" we need to know if the coach has any team yet.
  // If not, redirect them to create one first.
  const hasTeams = (t.teams || 0) > 0;
  const goAddFee = () => hasTeams ? navigate("/coach/charges/new") : navigate("/coach/teams");
  const goAddPlayer = () => navigate("/coach/teams");

  return (
    <AppLayout>
      <TrialBanner plan={plan} />
      <BetaBanner />

      {/* First-run onboarding checklist — auto-hides when all 4 steps done */}
      <OnboardingChecklist totals={t} />

      {/* Greeting */}
      <div className="mt-5">
        <h1 className="font-heading text-3xl font-bold text-ink-900 sm:text-4xl">Hi, {firstName}</h1>
        <p className="mt-1 text-base text-ink-600">Here&apos;s what needs you today.</p>
        {error ? <p className="mt-1 text-xs text-rose-600" data-testid="dashboard-error">{error}</p> : null}
      </div>

      {/* Today's Snapshot — three plain-English numbers, no graphs */}
      <section className="mt-6 rounded-2xl border border-ink-100 bg-white p-5 shadow-sm" data-testid="snapshot-card">
        <p className="text-xs font-bold uppercase tracking-wide text-ink-500">Today&apos;s Snapshot</p>
        <div className="mt-2 divide-y divide-ink-100">
          <SnapshotRow
            label="Still owed"
            value={formatCurrency(t.unpaid || 0)}
            tone={(t.unpaid || 0) > 0 ? "rose" : "ink"}
            testid="snap-still-owed"
          />
          <SnapshotRow
            label={`Unpaid ${terms.athletes}`}
            value={t.unpaid_players || 0}
            tone={(t.unpaid_players || 0) > 0 ? "rose" : "ink"}
            testid="snap-unpaid-players"
          />
          <SnapshotRow
            label="Payments to review"
            value={t.submitted_count || 0}
            tone={(t.submitted_count || 0) > 0 ? "amber" : "ink"}
            testid="snap-needs-approval"
          />
        </div>
      </section>

      {/* Four big actions — what the coach can do right now */}
      <section className="mt-6" data-testid="home-actions">
        <p className="text-xs font-bold uppercase tracking-wide text-ink-500">Quick actions</p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <ActionButton
            to="/coach/payments?status=submitted"
            icon={Inbox}
            label="Review Payments"
            sub={(t.submitted_count || 0) > 0 ? `${t.submitted_count} to review` : "Check parent submissions"}
            accent="amber"
            testid="action-review-payments"
          />
          <ActionButton
            to="/coach/payments?status=unpaid"
            icon={Send}
            label="Send Reminders"
            sub={(t.unpaid_players || 0) > 0 ? `${t.unpaid_players} ${t.unpaid_players === 1 ? `${terms.athlete} still owes` : `${terms.athletes} still owe`}` : `Nudge anyone who still owes`}
            accent="brand"
            testid="action-send-reminders"
          />
          <ActionButton
            onClick={goAddFee}
            icon={Plus}
            label="Add Fee"
            sub={hasTeams ? "Dues, uniforms, tournaments" : `Create a ${terms.team} first`}
            accent="ink"
            testid="action-add-fee"
          />
          <ActionButton
            onClick={goAddPlayer}
            icon={UserPlus}
            label={terms.add_athlete}
            sub={hasTeams ? `Open a ${terms.team} to add ${terms.athletes}` : `Create a ${terms.team} first`}
            accent="ink"
            testid="action-add-player"
          />
        </div>
      </section>

      {/* Recent activity — short, friendly list */}
      {(data.activity || []).length > 0 ? (
        <section className="mt-8" data-testid="home-recent">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold text-ink-900">Recent activity</h2>
            <Link to="/coach/payments" className="text-sm font-semibold text-brand hover:underline" data-testid="home-recent-view-all">
              See all <ChevronRight className="ml-0.5 inline h-3.5 w-3.5 align-[-2px]" />
            </Link>
          </div>
          <ul className="mt-3 divide-y divide-ink-100 overflow-hidden rounded-xl border border-ink-100 bg-white shadow-sm">
            {(data.activity || []).slice(0, 4).map((r) => {
              const statusLabel =
                r.status === "confirmed" ? "Paid"
                : r.status === "submitted" ? "To review"
                : r.status === "rejected" ? "Rejected"
                : r.status === "waived"  ? "Waived"
                : "Unpaid";
              const statusTone =
                r.status === "confirmed" ? "text-emerald-700"
                : r.status === "submitted" ? "text-amber-700"
                : r.status === "rejected" ? "text-rose-700"
                : "text-ink-600";
              return (
                <li key={r.id} className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-ink-100/30" data-testid={`home-activity-${r.id}`}>
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-ink-900">
                      {r.player?.first_name} {r.player?.last_name}
                    </p>
                    <p className="truncate text-xs text-ink-500">
                      {r.charge?.title} · {formatCurrency(r.amount)}
                    </p>
                  </div>
                  <span className={`shrink-0 text-xs font-bold uppercase tracking-wide ${statusTone}`}>{statusLabel}</span>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}
    </AppLayout>
  );
}
